import { Box, Button, Card, CardContent, List, ListItem, Typography } from "@mui/material";
import { useCreateRecord } from "../../api/useCreateRecord";
import { useUpdateRecord } from "../../api/useUpdateRecord";
import { useForm } from "react-hook-form";
import { useState } from "react";
import { Wysiwyg } from "../wysiwyg/Wysiwyg";
import { differenceInDays, format } from "date-fns";
import { Avatar } from "../fieldsViews/fields/Avatar";
import { useField } from "../../api/useField";
import { WysiwygField } from "../fieldsViews/fields/WysiwygField";
import { useUser } from "../../api/useAuth";
import { isRoleAdmin } from "../../utils/rolesUtils";

const canEditComment = (dateCreated: string) => {
    const daysSinceCreation = differenceInDays(new Date(), new Date(dateCreated));
    return daysSinceCreation < 1;
};

export type Comment = {
    id: string;
    content: string;
    user_created: {
        id: string;
        avatar?: string;
        first_name: string;
        last_name: string;
    };
    date_created: string;
    date_updated: string;
};

type CommentsProps = {
    commentsEntity: string;
    comments: Array<Comment>;
    relatedEntityField: string;
    relatedEntityId: number;
    relatedEntity: string;
    wysiwygCanMention: boolean;
    onInteraction?: () => void;
    readOnly?: boolean;
};

export const Comments = ({ comments, commentsEntity, relatedEntityField, relatedEntityId, relatedEntity, wysiwygCanMention, onInteraction, readOnly }: CommentsProps) => {
    const createRecord = useCreateRecord({ queryInvalidatePrefix: relatedEntity });
    const updateRecord = useUpdateRecord({ queryInvalidatePrefix: relatedEntity });
    const createForm = useForm();
    const editForm = useForm();
    const { formatedField: wysiwygField } = useField(commentsEntity, "content");
    const { data: user } = useUser();
    const userIsAdmin = isRoleAdmin(user?.role?.id);

    const [editComment, setEditingComment] = useState({
        id: "",
        content: "",
    });

    if (!wysiwygField) return null;

    const handleCommentSubmit = async (type: string) => {
        const commentText = createForm.getValues("content") || editForm.getValues("content");

        if (!commentText) {
            alert("Tem que adicionar comentário");
            return;
        }
        if (type === "create") {
            await createRecord.mutateAsync({
                entity: commentsEntity,
                data: {
                    content: commentText,
                    [relatedEntityField]: relatedEntityId,
                },
            });
        }

        if (type === "edit") {
            await updateRecord.mutateAsync({
                entity: commentsEntity,
                id: editComment.id,
                data: {
                    content: commentText,
                    [relatedEntityField]: relatedEntityId,
                },
            });
            setEditingComment({
                id: "",
                content: "",
            });
        }

        createForm.reset();

        if (onInteraction) {
            onInteraction();
        }
    };

    const editCommentSelectedComment = (id: string, content: string) => {
        editForm.setValue("content", content || "");
        setEditingComment({
            id: id,
            content: content,
        });
    };

    return (
        <Box>
            <Typography variant="h5">Comentários:</Typography>
            <List>
                {comments.map((comment: Comment) => (
                    <ListItem id={`commentId-${comment.id}`} key={comment.id} disableGutters>
                        <Card variant="outlined" sx={{ backgroundColor: "#f7f2f2", width: "100%" }}>
                            <CardContent>
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                    }}
                                >
                                    <Box sx={{ display: "flex", alignItems: "center" }}>
                                        <Avatar value={comment.user_created.avatar} />
                                        <Typography variant="body1" color="text.secondary" sx={{ ml: 2 }}>
                                            {comment.user_created.first_name} {comment.user_created.last_name}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ display: "flex", alignItems: "center" }}>
                                        <Typography variant="body2" color="text.secondary">
                                            {format(new Date(comment.date_updated || comment.date_created), "dd/MM/yyyy HH:mm")}
                                        </Typography>
                                        {!readOnly && (editComment.id === comment.id ? (
                                            <Button
                                                variant="text"
                                                sx={{ ml: 1 }}
                                                onClick={() => setEditingComment({ id: "", content: "" })}
                                            >
                                                Cancelar
                                            </Button>
                                        ) : (
                                            (userIsAdmin || canEditComment(comment.date_created)) && <Button
                                                variant="text"
                                                sx={{ ml: 1 }}
                                                onClick={() => editCommentSelectedComment(comment.id, comment.content)}
                                            >
                                                Editar
                                            </Button>
                                        ))}
                                    </Box>
                                </Box>
                                {editComment.id && comment.id === editComment.id ? (
                                    <Box p={4}>
                                        <Wysiwyg
                                            entityForm={editForm}
                                            size="200px"
                                            initialValue={comment.content}
                                            field={wysiwygField}
                                            canMention={wysiwygCanMention}
                                        />
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            onClick={() => handleCommentSubmit("edit")}
                                            fullWidth
                                            sx={{ mt: 2 }}
                                        >
                                            Atualizar Comentário
                                        </Button>
                                    </Box>
                                ) : (
                                    <WysiwygField field={wysiwygField} value={comment.content} key={comment.id} label="" fieldView="group" />
                                )}
                            </CardContent>
                        </Card>
                    </ListItem>
                ))}
            </List>
            {!readOnly && (
                <>
                    <Wysiwyg entityForm={createForm} size="200px" field={wysiwygField} initialValue={""} canMention={wysiwygCanMention}/>
                    <Button variant="contained" color="primary" onClick={() => handleCommentSubmit("create")} fullWidth sx={{ mt: 2 }}>
                        Adicionar Comentário
                    </Button>
                </>
            )}
        </Box>
    );
};
